import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import './RecurringSeriesPage.css';

const emptySchedule = () => ({ scheduled_date: '', label: '', search_query: '' });

function nextWeekdays(count = 30) {
  const dates = [];
  const cursor = new Date();
  cursor.setHours(12, 0, 0, 0);
  while (dates.length < count) {
    cursor.setDate(cursor.getDate() + 1);
    if (cursor.getDay() === 0 || cursor.getDay() === 6) continue;
    const localDate = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
    dates.push({
      scheduled_date: localDate,
      label: `Day ${dates.length + 1}`,
      search_query: ''
    });
  }
  return dates;
}

function senderLabel(sender) {
  return sender.email || sender.name || sender.public_identifier || sender.account_id;
}

function RecurringSeriesPage({ accountId }) {
  const [series, setSeries] = useState([]);
  const [senders, setSenders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({
    name: '', template_campaign_id: '', sender_account_ids: accountId ? [accountId] : [],
    ranking_criteria: '', search_limit: 1000, score_threshold: 60, priority: 5,
    network_distance: [2, 3], schedules: [emptySchedule()]
  });

  const usableSenders = useMemo(() => senders.filter(sender => !['CREDENTIALS', 'DISCONNECTED', 'ERROR', 'FAILED'].includes(String(sender.status || '').toUpperCase())), [senders]);

  const load = async () => {
    setLoading(true);
    try {
      const [seriesResponse, sendersResponse, campaignsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/recurring-series`),
        axios.get(`${API_BASE_URL}/linkedin/accounts`),
        accountId ? axios.get(`${API_BASE_URL}/campaigns`, { params: { account_id: accountId } }) : Promise.resolve({ data: { campaigns: [] } })
      ]);
      setSeries(seriesResponse.data.series || []);
      setSenders(sendersResponse.data.senders || []);
      setTemplates(campaignsResponse.data.campaigns || []);
    } catch (error) {
      setMessage(error.response?.data?.message || '加载 Recurring Series 失败');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [accountId]);

  const updateSchedule = (index, field, value) => {
    setForm(current => ({ ...current, schedules: current.schedules.map((schedule, itemIndex) => itemIndex === index ? { ...schedule, [field]: value } : schedule) }));
  };

  const toggleSender = (id) => {
    setForm(current => ({ ...current, sender_account_ids: current.sender_account_ids.includes(id)
      ? current.sender_account_ids.filter(account => account !== id)
      : [...current.sender_account_ids, id] }));
  };

  const toggleDistance = (distance) => {
    setForm(current => ({ ...current, network_distance: current.network_distance.includes(distance)
      ? current.network_distance.filter(value => value !== distance)
      : [...current.network_distance, distance].sort() }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const response = await axios.post(`${API_BASE_URL}/recurring-series`, form);
      setMessage(response.data.message || 'Recurring Series 已创建');
      setForm({ ...form, name: '', ranking_criteria: '', schedules: [emptySchedule()] });
      await load();
    } catch (error) {
      setMessage(error.response?.data?.message || '创建失败');
    } finally { setSaving(false); }
  };

  const changeStatus = async (id, status) => {
    try {
      await axios.put(`${API_BASE_URL}/recurring-series/${id}/status`, { status });
      await load();
    } catch (error) { setMessage(error.response?.data?.message || '状态更新失败'); }
  };

  const runNow = async (id) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/recurring-series/${id}/run-now`);
      setMessage(response.data.message);
    } catch (error) { setMessage(error.response?.data?.message || '运行请求失败'); }
  };

  const retry = async (seriesId, scheduleId) => {
    try {
      await axios.post(`${API_BASE_URL}/recurring-series/${seriesId}/schedules/${scheduleId}/retry`);
      await load();
    } catch (error) { setMessage(error.response?.data?.message || '重试失败'); }
  };

  return (
    <main className="series-page">
      <div className="series-page-header">
        <div><h2>Recurring Series</h2><p>按固定日期搜索、评分并生成独立 Campaign。</p></div>
      </div>
      {message && <div className="series-message" role="status">{message}</div>}

      <section className="series-form-section">
        <h3>New Series</h3>
        <form onSubmit={submit} className="series-form">
          <label>Series name<input value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} required placeholder="例如：North America student PMF" /></label>
          <label>Workflow template
            <select value={form.template_campaign_id} onChange={event => setForm({ ...form, template_campaign_id: event.target.value })} required>
              <option value="">Select an existing Campaign</option>
              {templates.map(template => <option key={template.id} value={template.id}>{template.name}</option>)}
            </select>
          </label>
          <fieldset className="series-fieldset"><legend>Participating Senders</legend>
            <div className="series-checkboxes">{usableSenders.map(sender => <label key={sender.account_id} className="series-check"><input type="checkbox" checked={form.sender_account_ids.includes(sender.account_id)} onChange={() => toggleSender(sender.account_id)} />{senderLabel(sender)}</label>)}</div>
          </fieldset>
          <label className="series-full">Ranking criteria<textarea value={form.ranking_criteria} onChange={event => setForm({ ...form, ranking_criteria: event.target.value })} required rows="4" placeholder="Describe current students / recent graduates, target profile, qualifications, and exclusions." /></label>
          <div className="series-form-grid">
            <label>Search limit<input type="number" min="1" max="1000" value={form.search_limit} onChange={event => setForm({ ...form, search_limit: event.target.value })} /></label>
            <label>Score threshold<input type="number" min="0" max="100" value={form.score_threshold} onChange={event => setForm({ ...form, score_threshold: event.target.value })} /></label>
            <label>Campaign priority<input type="number" min="1" max="10" value={form.priority} onChange={event => setForm({ ...form, priority: event.target.value })} /></label>
          </div>
          <fieldset className="series-fieldset"><legend>Network distance</legend><div className="series-checkboxes">{[1, 2, 3].map(distance => <label className="series-check" key={distance}><input type="checkbox" checked={form.network_distance.includes(distance)} onChange={() => toggleDistance(distance)} />{distance === 1 ? '1st' : distance === 2 ? '2nd' : '3rd+'}</label>)}</div></fieldset>
          <div className="series-schedules-heading"><h4>Fixed-date searches</h4><div><button type="button" className="series-button secondary" onClick={() => setForm({ ...form, schedules: nextWeekdays() })}>Generate 30 weekdays</button><button type="button" className="series-button secondary" onClick={() => setForm({ ...form, schedules: [...form.schedules, emptySchedule()] })}>Add date</button></div></div>
          <div className="series-schedule-list">{form.schedules.map((schedule, index) => <div className="series-schedule-row" key={`${schedule.scheduled_date}-${index}`}>
            <input aria-label={`Date ${index + 1}`} type="date" value={schedule.scheduled_date} onChange={event => updateSchedule(index, 'scheduled_date', event.target.value)} required />
            <input aria-label={`Label ${index + 1}`} value={schedule.label} onChange={event => updateSchedule(index, 'label', event.target.value)} placeholder="Label, e.g. NYU" />
            <textarea aria-label={`Search query ${index + 1}`} value={schedule.search_query} onChange={event => updateSchedule(index, 'search_query', event.target.value)} placeholder="Natural-language LinkedIn search query" required />
            <button type="button" className="series-icon-button" aria-label="Remove date" title="Remove date" disabled={form.schedules.length === 1} onClick={() => setForm({ ...form, schedules: form.schedules.filter((_, itemIndex) => itemIndex !== index) })}>×</button>
          </div>)}</div>
          <button className="series-button primary" type="submit" disabled={saving}>{saving ? 'Creating...' : 'Create & activate Series'}</button>
        </form>
      </section>

      <section className="series-list-section"><h3>Series</h3>{loading ? <p>Loading...</p> : series.length === 0 ? <p className="series-muted">No Recurring Series yet.</p> : <div className="series-list">{series.map(item => <article className="series-item" key={item.id}>
        <header><div><h4>{item.name}</h4><span className={`series-status ${item.status}`}>{item.status}</span></div><div className="series-actions">{item.status === 'active' ? <button className="series-button secondary" onClick={() => changeStatus(item.id, 'paused')}>Pause</button> : <button className="series-button secondary" onClick={() => changeStatus(item.id, 'active')}>Resume</button>}<button className="series-button secondary" onClick={() => runNow(item.id)}>Check now</button><button className="series-button danger" onClick={() => changeStatus(item.id, 'stopped')}>Stop future runs</button></div></header>
        <p>{item.ranking_criteria}</p><div className="series-meta">{item.sender_account_ids.length} Senders · Up to {item.search_limit} candidates · Score ≥ {item.score_threshold}</div>
        <div className="series-run-table">{item.schedules.map(schedule => <div className="series-run-row" key={schedule.id}><span>{schedule.scheduled_date}</span><span>{schedule.label || 'Untitled search'}</span><span>{schedule.status}</span><span>{schedule.assigned_account_id || 'Awaiting sender'}</span><span>{schedule.qualified_count ?? 0} qualified</span>{schedule.campaign_id ? <a href={`/campaigns/${schedule.campaign_id}/status`}>{schedule.campaign_name || 'Open Campaign'}</a> : schedule.status === 'failed' ? <button className="series-text-button" onClick={() => retry(item.id, schedule.id)}>Retry</button> : <span />}</div>)}</div>
      </article>)}</div>}</section>
    </main>
  );
}

export default RecurringSeriesPage;
